/**
 * UserFront와 동일한 API 서비스 구조.
 * 홈페이지 단계에서는 UI만 사용하고, 로그인/게임 연동 시 각 메서드를 호출합니다.
 */
import axios from './http'
import i18n from '@/utils/i18n'
import { getMemberId, isLoggedIn } from '@/utils/auth'

const SUPPORTED_LANGS = ['en', 'ko', 'zh', 'ja']

function currentLang () {
  const loc = (i18n && i18n.locale) ? String(i18n.locale).toLowerCase() : ''
  return SUPPORTED_LANGS.indexOf(loc) !== -1 ? loc : 'ko'
}

function apiBase () {
  return process.env.VUE_APP_BASE_API_URL
}

export const AuthService = {
  getAdminUrl () {
    return process.env.VUE_APP_BASE_ADMIN_URL
  },
  login (data) {
    return axios.post('gateway/login', data, {
      baseURL: apiBase(),
      skipLoading: true
    })
  },
  logout () {
    return axios.post('gateway/logout', {}, {
      baseURL: apiBase(),
      skipLoading: true
    })
  },
  getUserInfo () {
    return axios.post('gateway/user-info', {
      memeber: getMemberId()
    }, {
      baseURL: apiBase(),
      skipLoading: true
    })
  },
  signup (data) {
    return axios.post('gateway/signup/', data, {
      baseURL: apiBase(),
      skipLoading: true
    })
  },
  sendPhoneOtp (phoneNumber) {
    return axios.post('gateway/phone', { phoneNumber }, {
      baseURL: apiBase(),
      skipLoading: true
    })
  },
  checkPhoneOtp (phoneNumber, code) {
    return axios.post('gateway/check', { phoneNumber, code }, {
      baseURL: apiBase(),
      skipLoading: true
    })
  },
  getBetHistory (payload) {
    return axios.post('gateway/detail', payload, {
      baseURL: apiBase(),
      skipLoading: true
    })
  }
}

export const GameService = {
  getProviderList () {
    return axios.post('game/providers', {}, {
      baseURL: apiBase(),
      skipLoading: true
    })
  },
  providerList () {
    return axios.post('user/game/provider_list', {}, {
      baseURL: apiBase(),
      skipLoading: true
    })
  },
  gameList (providerId, category) {
    const path = isLoggedIn() ? 'game/real-games' : 'game/games'
    return axios.post(path + '?' + currentLang(), {
      provider: Number(providerId),
      category: Number(category)
    }, {
      baseURL: apiBase(),
      skipLoading: true
    })
  },
  getHotGameList () {
    return axios.post('game/hots?' + currentLang(), {}, {
      baseURL: apiBase(),
      skipLoading: true
    })
  },
  getFishingList () {
    return axios.post('game/fishings?' + currentLang(), {}, {
      baseURL: apiBase(),
      skipLoading: true
    })
  },
  startNewGame (provider, symbol, platform, category, playMode, vendorId = -1) {
    const parsedVendorId = Number(vendorId)
    return axios.post('game/launch?' + currentLang(), {
      provider,
      game_symbol: symbol,
      platform,
      category,
      playMode,
      vendor_id: Number.isFinite(parsedVendorId) ? parsedVendorId : -1
    }, {
      baseURL: apiBase(),
      skipLoading: true
    })
  },
  startDemoGame (provider, symbol, playMode = 'fun') {
    return axios.post('game/demo/launch?' + currentLang(), {
      provider: Number(provider),
      game_symbol: symbol,
      playMode
    }, {
      baseURL: apiBase(),
      skipLoading: true
    })
  },
  getBalanceApi () {
    return axios.post('gateway/runtime', {}, {
      baseURL: apiBase(),
      skipLoading: true
    })
  },
  getJackpotApi (options = {}) {
    return axios.post('gateway/jackpot', {}, {
      baseURL: apiBase(),
      skipLoading: true,
      skipSessionGuard: options.skipSessionGuard !== false
    })
  }
}

export const WalletService = {
  chargeRequest ({ amount, account = '', memo = '' }) {
    const data = {
      amount: Number(amount),
      account: account ? String(account).trim() : '',
      memo: memo ? String(memo).trim() : ''
    }
    return axios.post('wallet/charge_req', data, {
      baseURL: apiBase(),
      skipLoading: true
    })
  },
  chargeList ({ offset = 0, limit = 20, loginid = '' } = {}) {
    const data = {
      search_key: 'loginid',
      search_val: loginid ? String(loginid).trim() : '',
      offset: Number(offset),
      limit: Number(limit),
      order_key: 'regdate',
      order_dir: 0
    }
    return axios.post('wallet/charge_list', data, {
      baseURL: apiBase(),
      skipLoading: true
    })
  },
  withdrawRequest ({ amount, bankAccount = '', bankName = '', bankMaster = '', memo = '' }) {
    const data = {
      amount: Number(amount),
      bank_account: bankAccount ? String(bankAccount).trim() : '',
      bank_name: bankName ? String(bankName).trim() : '',
      bank_master: bankMaster ? String(bankMaster).trim() : '',
      memo: memo ? String(memo).trim() : ''
    }
    return axios.post('wallet/withdraw_req', data, {
      baseURL: apiBase(),
      skipLoading: true
    })
  },
  withdrawList ({ offset = 0, limit = 20, loginid = '' } = {}) {
    const data = {
      search_key: 'loginid',
      search_val: loginid ? String(loginid).trim() : '',
      offset: Number(offset),
      limit: Number(limit),
      order_key: 'regdate',
      order_dir: 0
    }
    return axios.post('wallet/withdraw_list', data, {
      baseURL: apiBase(),
      skipLoading: true
    })
  },
  compRequest ({ amount, memo = '' }) {
    const data = {
      amount: Number(amount),
      memo: memo ? String(memo).trim() : ''
    }
    return axios.post('wallet/comp_req', data, {
      baseURL: apiBase(),
      skipLoading: true
    })
  }
}

export const SystemService = {
  getSiteSettingInfo () {
    return axios.post('system/info', {}, {
      baseURL: apiBase(),
      skipLoading: true
    })
  },
  getBannerList () {
    return axios.post('system/banner_list', {}, {
      baseURL: apiBase(),
      skipLoading: true
    })
  },
  getQuickNoticeList () {
    return axios.post('system/notice_list', {
      type: 2,
      offset: 0,
      limit: 50
    }, {
      baseURL: apiBase(),
      skipLoading: true
    })
  },
  getNoticeList (start, count, type = 0) {
    return axios.post('system/notice_list', {
      type: Number(type),
      offset: Number(start),
      limit: Number(count)
    }, {
      baseURL: apiBase(),
      skipLoading: true
    })
  },
  getEventList (start, count) {
    return axios.post('system/notice_list', {
      type: 4,
      offset: Number(start),
      limit: Number(count)
    }, {
      baseURL: apiBase(),
      skipLoading: true
    })
  },
  getNoticeDetail (id) {
    if (id == null || id === '') return Promise.resolve(null)
    return axios.post('system/notice_info', {
      idx: Number(id)
    }, {
      baseURL: apiBase(),
      skipLoading: true
    })
  },
  getQuestionList (start, count) {
    return axios.post('system/question_list', {
      offset: Number(start),
      limit: Number(count)
    }, {
      baseURL: apiBase(),
      skipLoading: true
    })
  },
  getQuestionDetail (id) {
    if (id == null || id === '') return Promise.resolve(null)
    return axios.post('system/question_info', {
      idx: Number(id)
    }, {
      baseURL: apiBase(),
      skipLoading: true
    })
  },
  createQuestion (title, question) {
    return axios.post('system/question_create', {
      title: String(title || ''),
      question: String(question || '')
    }, {
      baseURL: apiBase(),
      skipLoading: true
    })
  },
  getSlipList (offset = 0, limit = 20, status = 2) {
    return axios.post('system/slip_list', {
      offset: Number(offset),
      limit: Number(limit),
      status: Number(status)
    }, {
      baseURL: apiBase(),
      skipLoading: true
    })
  },
  getSlipDetail (idx) {
    if (idx == null || idx === '') return Promise.resolve(null)
    return axios.post('system/slip_info', {
      idx: Number(idx)
    }, {
      baseURL: apiBase(),
      skipLoading: true
    })
  }
}
